import React from 'react';
import './EventCard.css';
import { Bet } from './BetCard';
import { useUserTimezone, formatWithTz } from '../hooks/useUserTimezone';
import { Badge, LockedValue } from './ui';

export interface EventSummary {
  eventId: string;
  sport: string;
  eventTitle: string;
  commenceTime: string;
  isLive: boolean;
  numBets: number; // total outcomes (bets) for the event
  positiveEvCount: number; // number of bets with positive EV
  bestBet: Bet | null;
  worstBet: Bet | null;
  latestMarketLastUpdate: string | null;
}

export const buildEventSummaries = (bets: Bet[], onlyEventIdsInOrder?: string[]): EventSummary[] => {
  const eventIdToBets = new Map<string, Bet[]>();
  const eventIdToMeta = new Map<string, { commenceTime: number; sport: string; eventTitle: string; isLive: boolean; latestUpdate: number | null }>();

  for (const bet of bets) {
    const eventId = bet.id.split('|')[0];
    if (!eventIdToBets.has(eventId)) eventIdToBets.set(eventId, []);
    eventIdToBets.get(eventId)!.push(bet);
    const ct = new Date(bet.commenceTime).getTime();
    if (!eventIdToMeta.has(eventId)) {
      eventIdToMeta.set(eventId, {
        commenceTime: ct,
        sport: bet.sport,
        eventTitle: bet.event,
        isLive: bet.isLive,
        latestUpdate: bet.marketLastUpdate ? new Date(bet.marketLastUpdate).getTime() : null,
      });
    } else {
      const meta = eventIdToMeta.get(eventId)!;
      // Keep earliest commence time and live state if any bet is live
      meta.commenceTime = Math.min(meta.commenceTime, ct);
      meta.isLive = meta.isLive || bet.isLive;
      const upd = bet.marketLastUpdate ? new Date(bet.marketLastUpdate).getTime() : null;
      if (upd !== null) meta.latestUpdate = meta.latestUpdate === null ? upd : Math.max(meta.latestUpdate, upd);
    }
  }

  const allEventIds = onlyEventIdsInOrder && onlyEventIdsInOrder.length > 0
    ? onlyEventIdsInOrder
    : Array.from(eventIdToBets.keys()).sort((a, b) => {
        const ams = eventIdToMeta.get(a)?.commenceTime ?? 0;
        const bms = eventIdToMeta.get(b)?.commenceTime ?? 0;
        return ams - bms;
      });

  const summaries: EventSummary[] = [];
  for (const eventId of allEventIds) {
    const group = eventIdToBets.get(eventId) ?? [];
    if (group.length === 0) continue;

    let pos = 0;
    let best: Bet | null = null;
    let worst: Bet | null = null;

    for (const b of group) {
      if (b.expectedValue !== null) {
        // Count as positive only if EV >= 0.0001 (0.01%)
        if (b.expectedValue >= 0.0001) pos += 1;
        if (!best || (best.expectedValue ?? -Infinity) < (b.expectedValue ?? -Infinity)) best = b;
        if (!worst || (worst.expectedValue ?? Infinity) > (b.expectedValue ?? Infinity)) worst = b;
      }
    }

    const meta = eventIdToMeta.get(eventId)!;

    summaries.push({
      eventId,
      sport: meta.sport,
      eventTitle: meta.eventTitle,
      commenceTime: new Date(meta.commenceTime).toISOString(),
      isLive: meta.isLive,
      numBets: group.length,
      positiveEvCount: pos,
      bestBet: best,
      worstBet: worst,
      latestMarketLastUpdate: meta.latestUpdate !== null ? new Date(meta.latestUpdate).toISOString() : null,
    });
  }

  return summaries;
};

interface EventCardProps {
  summary: EventSummary;
  analyticsVisibility?: 'full' | 'teaser' | 'locked';
}

const EventCard: React.FC<EventCardProps> = ({ summary, analyticsVisibility = 'full' }) => {
  const { tz } = useUserTimezone();
  const formatTime = (timeString: string) => formatWithTz(timeString, tz);

  const toAmericanOdds = (decimalOdds: number): string => {
    if (!isFinite(decimalOdds) || decimalOdds <= 1) return 'N/A';
    if (decimalOdds >= 2) return `+${Math.round((decimalOdds - 1) * 100)}`;
    return `${Math.round(-100 / (decimalOdds - 1))}`;
  };

  const formatPct = (v: number) => `${(v * 100).toFixed(2)}%`;

  return (
    <div className={`event-card ${summary.isLive ? 'live' : ''}`}>
      <div className="event-header">
        <Badge variant="sport" className="sport-badge">{summary.sport}</Badge>
        {summary.isLive && <Badge variant="live" className="live-badge">LIVE</Badge>}
      </div>
      <div className="event-content">
        <h3 className="event-title left">
          <span className="event-title-stack">
            {(() => {
              const title = summary.eventTitle;
              let away = title;
              let home = '';
              if (title.includes(' @ ')) {
                const parts = title.split(' @ ');
                away = parts[0] || title;
                home = parts[1] || '';
              } else if (title.includes(' vs ')) {
                const parts = title.split(' vs ');
                home = parts[0] || '';
                away = parts[1] || title;
              }
              return (
                <>
                  <span className="away-team">{away}</span>
                  <span className="at-symbol">@</span>
                  <span className="home-team">{home}</span>
                </>
              );
            })()}
          </span>
        </h3>
        <div className="detail-row">
          <span className="label">Event Start:</span>
          <span className="value nowrap">{formatTime(summary.commenceTime)}</span>
        </div>
        {summary.latestMarketLastUpdate && (
          <div className="detail-row">
            <span className="label">Odds Last Updated:</span>
            <span className="value nowrap">{formatTime(summary.latestMarketLastUpdate)}</span>
          </div>
        )}
        <div className="detail-row">
          <span className="label">Bets:</span>
          <span className="value">{summary.numBets}</span>
        </div>
        <div className="detail-row">
          <span className="label">Bets with +EV:</span>
          <span className="value">{summary.positiveEvCount}</span>
        </div>
        {analyticsVisibility === 'full' && (
          <div className="event-analytics analytics">
            <div className="bet best">
              <div className="analytics-title">Best Bet</div>
              {summary.bestBet ? (
                <div className="bet-line">
                  <div className="bet-row top">
                    <span className="outcome">{summary.bestBet.outcome}{summary.bestBet.market === 'Moneyline' ? ' ML' : ''}</span>
                    <span className="odds">{toAmericanOdds(summary.bestBet.odds)}</span>
                    <span className="bookmaker">{summary.bestBet.bookmaker}</span>
                  </div>
                  <div className="bet-row bottom">
                    <span className="win-prob">
                      <span className="label">Win Probability:</span> {summary.bestBet.trueWinProbability !== null ? formatPct(summary.bestBet.trueWinProbability) : 'N/A'}
                    </span>
                    <span className={`ev ${summary.bestBet.expectedValue !== null && summary.bestBet.expectedValue >= 0.0001 ? 'positive' : 'negative'}`}><span className="label">EV:</span> {summary.bestBet.expectedValue !== null ? formatPct(summary.bestBet.expectedValue) : 'N/A'}</span>
                  </div>
                </div>
              ) : (
                <div className="bet-line muted">N/A</div>
              )}
            </div>
            <div className="bet worst">
              <div className="analytics-title">Worst Bet</div>
              {summary.worstBet ? (
                <div className="bet-line">
                  <div className="bet-row top">
                    <span className="outcome">{summary.worstBet.outcome}{summary.worstBet.market === 'Moneyline' ? ' ML' : ''}</span>
                    <span className="odds">{toAmericanOdds(summary.worstBet.odds)}</span>
                    <span className="bookmaker">{summary.worstBet.bookmaker}</span>
                  </div>
                  <div className="bet-row bottom">
                    <span className="win-prob">
                      <span className="label">Win Probability:</span> {summary.worstBet.trueWinProbability !== null ? formatPct(summary.worstBet.trueWinProbability) : 'N/A'}
                    </span>
                    <span className="ev negative"><span className="label">EV:</span> {summary.worstBet.expectedValue !== null ? formatPct(summary.worstBet.expectedValue) : 'N/A'}</span>
                  </div>
                </div>
              ) : (
                <div className="bet-line muted">N/A</div>
              )}
            </div>
          </div>
        )}
        {analyticsVisibility === 'teaser' && (
          <div className="event-analytics analytics teaser">
            <div className="analytics-title">Market Snapshot</div>
            {summary.bestBet ? (
              <div className="bet-line">
                <div className="bet-row top">
                  <span className="outcome">{summary.bestBet.outcome}{summary.bestBet.market === 'Moneyline' ? ' ML' : ''}</span>
                  <span className="odds">{toAmericanOdds(summary.bestBet.odds)}</span>
                  <span className="bookmaker">{summary.bestBet.bookmaker}</span>
                </div>
              </div>
            ) : (
              <div className="bet-line muted">N/A</div>
            )}
            <div className="event-teaser-row">
              <span>Best and worst EV breakdown</span>
              <LockedValue />
            </div>
          </div>
        )}
        {analyticsVisibility === 'locked' && (
          <div className="event-analytics analytics teaser">
            <div className="analytics-title">Advanced Breakdown</div>
            <div className="event-teaser-row">
              <span>Fair odds, probability, and EV</span>
              <LockedValue text="Locked" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventCard;


